import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router';

import {
  IonToolbar,
  IonContent,
  IonPage,
  IonTitle,
  IonMenuButton,
  IonButton,
  IonButtons,
  IonHeader,
  IonList,
  IonListHeader,
  IonItem,
  IonInput,
  IonLoading,
  IonSelect,
  IonSelectOption,
  getConfig,
  useIonToast,
} from '@ionic/react';

import './KoStorePage.scss';
import { connect } from '../data/connect';
import { KoStore } from '../models/KoStore';
import axiosInstance from '../data/axios/axiosInstance';
import KoStoreList from '../components/KoStoreList';

interface OwnProps {
  onTabChange: (event: CustomEvent) => void;
}

interface StateProps {
  mode: 'ios' | 'md';
  isAuthenticated: boolean;
}

interface DispatchProps {
}

type KoStorePageProps = OwnProps & StateProps & DispatchProps;

const KoStorePage: React.FC<KoStorePageProps> = ({
  isAuthenticated,
  onTabChange,
}) => {
  const [barcode, setBarcode] = useState('');
  const [racknum, setRacknum] = useState('');
  const [count, setCount] = useState(0);
  const [kostores, setKoStores] = useState<KoStore[]>([]);
  const [highlightOrderNum, setHighlightOrderNum] = useState(0);  
  const [loading, setLoading] = useState(false);
  const pageRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLIonInputElement>(null);
  const rackRef = useRef<HTMLIonInputElement>(null);
  const [presentToast] = useIonToast();
  const location = useLocation();

  const showToast = (message: string, duration = 3000, focusRef?: React.RefObject<any>) => {
    presentToast({
      message,
      duration,
      position: 'middle',
      buttons: [
        {
          text: 'Close',
          role: 'cancel',
          handler: () => {
            // Close 押下後にフォーカスを戻す
          if (focusRef) {
            setTimeout(() => {
              if (typeof focusRef.current?.setFocus === 'function') {
                focusRef.current.setFocus();
              } else if (typeof focusRef.current?.open === 'function') {
                focusRef.current.open();
              }
            }, 50);
          }
          },          
        },
      ],
    });
  };

  useEffect(() => {
    inputRef.current?.setFocus();
  }, [count]);

  const playBuzzer = () => {
    const buzzer = document.getElementById('buzzer') as HTMLAudioElement;
    if (buzzer) {
      buzzer.play();
    }
  };

  const playRing = () => {
    const ring = document.getElementById('ring') as HTMLAudioElement;
    if (ring) {
      ring.play();
    }
  };

  const handleBarcodeChange = async (event: CustomEvent) => {
    setBarcode((event.target as HTMLInputElement).value.toUpperCase());
  };

  const handleRacknumChange = (event: CustomEvent) => {
    setRacknum((event.target as HTMLInputElement).value.toUpperCase());
  };

  const handleKeyPress = async (event: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (event.key === 'Enter') {
      rackRef.current?.setFocus();
    }
  };

  const handleRackKeyPress = async (event: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (event.key === 'Enter') {
      inputRef.current?.setFocus();
    }
  };

  const handleProcess = async () => {
    if ( barcode === '' ) {
      showToast("オーダー番号を入力してください。",3000, inputRef);
      playBuzzer();
      inputRef.current?.setFocus();
      return;
    }
    // 数字のみかチェック（1桁以上の数字）
    const isNumeric = /^\d+$/.test(barcode);
    if (!isNumeric) {
      showToast("オーダー番号は数字以外入力できません。", 3000, inputRef);
      inputRef.current?.setFocus();
      return;
    }

    if ( racknum === "" ) {
      showToast("棚番を入力してください。", 3000, rackRef);
      playBuzzer();
      rackRef.current?.setFocus();
      return;
    }

    const orderNum = Number(barcode);
    setLoading(true);      
    try {
      const response = await axiosInstance.post('/kostores/', { order_num: orderNum, rack_num: racknum});
      if (response.status === 200 && response.data.result == "success") {
        setKoStores(response.data.kostores);
        setHighlightOrderNum(response.data.highlightordernum)
        setCount(response.data.kostores.length)
        setBarcode('');
        setRacknum('');

        inputRef.current?.setFocus();
        playRing();
      } else {
        inputRef.current?.setFocus();
        showToast("仮置き倉庫棚番入力エラー", 3000, inputRef);
        playBuzzer();
      }
      setLoading(false);
    } catch (error) {
      console.error('Error processing kostores:', error);
      playBuzzer();
      setLoading(false);
    }
  };

  const handleOrderNumClear = () => {
    setBarcode('');
    setRacknum("");
    inputRef.current?.setFocus();
  }

  const fetchInspections = async () => {
    try {
      const response = await axiosInstance.get('/kostore');
      if (response.status === 200) {
        setKoStores(response.data.kostores)
        setHighlightOrderNum(0)
        setCount(response.data.kostores.length)

        inputRef.current?.setFocus();
      }
    } catch (error) {
      console.error('Error fetching kostores:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated && location.pathname.includes("kostore")) {
      fetchInspections();
    }
  }, [isAuthenticated, onTabChange]);

  return (
    <IonPage ref={pageRef} id="kostore-page">
      <IonHeader translucent={true}>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>仮置き倉庫棚番入力作業</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen={true}>
        <audio id="buzzer" src="/assets/error.mp3" preload="auto"></audio>
        <audio id="ring" src="/assets/ok.mp3" preload="auto"></audio>
        <IonList>
          <IonItem>
            <IonInput label="オーダー番号" ref={inputRef} type="text" placeholder="000" value={barcode} onIonInput={handleBarcodeChange} onKeyDown={handleKeyPress}></IonInput>
            <IonButton color="primary" expand="full" slot='end' onClick={handleOrderNumClear}>クリア</IonButton>
          </IonItem>
          <IonItem>
            <IonInput label="棚番" ref={rackRef} value={racknum} onIonInput={handleRacknumChange} onKeyDown={handleRackKeyPress}></IonInput>
            <IonButton color="primary" expand="full" slot='end' onClick={handleProcess}>完了</IonButton>
          </IonItem>
        </IonList>
        <IonListHeader style={{ fontWeight: 'bold', fontSize: '22px' }}>{"仮置き倉庫棚番マスタ : " + count + "件"}</IonListHeader>
        <div style={{ overflowY: 'auto', height: 'calc(100% - 110px)' }}>
          <KoStoreList
            kostores={kostores}
            highlightOrderNum={highlightOrderNum}
          />
        </div>
        <IonLoading
          isOpen={loading}
          message={'Loading...'}
          onDidDismiss={() => setLoading(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default connect<OwnProps, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    mode: getConfig()!.get('mode'),
    isAuthenticated: state.user.isLoggedin,
  }),
  mapDispatchToProps: {
  },
  component: React.memo(KoStorePage),
});
